import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Download, ExternalLink, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BillingActivity({ user }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    fetchBillingActivity();
  }, []);

  const fetchBillingActivity = async () => {
    try {
      const response = await axios.get(`${API}/billing/activity?limit=50`);
      if (response.data.success) {
        setEvents(response.data.events);
      }
    } catch (error) {
      console.error('Error fetching billing activity:', error);
      toast.error('Failed to load billing activity');
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    if (events.length === 0) {
      toast.error('No events to export');
      return;
    }

    // Prepare CSV data
    const headers = ['Event Time', 'Type', 'Amount', 'Plan/Bundle', 'Idempotency Key', 'Status', 'Request ID'];
    const rows = events.map(event => [
      new Date(event.created_at).toLocaleString(),
      event.event_type,
      `${event.amount || 0} ${event.currency || 'USD'}`,
      event.plan_or_bundle || 'N/A',
      event.idempotency_key || 'N/A',
      event.status || 'completed',
      event.request_id || 'N/A'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `billing-activity-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast.success('CSV exported successfully');
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopiedId(label);
    toast.success(`${label} copied to clipboard`);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'applied':
      case 'completed':
        return '#47FFB0';
      case 'ignored-duplicate':
        return '#FFC107';
      case 'error':
      case 'failed':
        return '#FF6B6B';
      default:
        return '#4B9DFF';
    }
  };

  const formatEventType = (type) => {
    return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#FFFFFF' }}>
        <div className="text-xl" style={{ color: '#0B0B0B' }}>Loading billing activity...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#0B0B0B', fontFamily: 'Space Grotesk' }}>
            Billing Activity
          </h1>
          <p className="text-sm mt-1" style={{ color: '#0B0B0B', opacity: 0.7 }}>
            View all payment events and transactions
          </p>
        </div>
        <Button
          onClick={exportCSV}
          disabled={events.length === 0}
          style={{ background: '#47FFB0', color: '#0D0D0D' }}
        >
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {events.length === 0 ? (
        <Card className="glass-card border-primary/20">
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" 
                 style={{ background: 'rgba(75, 157, 255, 0.1)' }}>
              <Download className="w-8 h-8" style={{ color: '#4B9DFF' }} />
            </div>
            <p className="text-lg mb-2" style={{ color: '#0B0B0B' }}>No billing events yet</p>
            <p className="text-sm" style={{ color: '#0B0B0B', opacity: 0.7 }}>
              Buy a contract bundle to see activity here.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="glass-card border-primary/20">
          <CardHeader>
            <CardTitle style={{ color: '#4B9DFF' }}>Recent Activity</CardTitle>
            <CardDescription style={{ color: '#0B0B0B' }}>
              Last {events.length} billing events
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Desktop View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(75, 157, 255, 0.2)' }}>
                    <th className="text-left py-3 px-2" style={{ color: '#0B0B0B', fontSize: '14px', fontWeight: '600' }}>Event Time</th>
                    <th className="text-left py-3 px-2" style={{ color: '#0B0B0B', fontSize: '14px', fontWeight: '600' }}>Type</th>
                    <th className="text-left py-3 px-2" style={{ color: '#0B0B0B', fontSize: '14px', fontWeight: '600' }}>Amount</th>
                    <th className="text-left py-3 px-2" style={{ color: '#0B0B0B', fontSize: '14px', fontWeight: '600' }}>Plan/Bundle</th>
                    <th className="text-left py-3 px-2" style={{ color: '#0B0B0B', fontSize: '14px', fontWeight: '600' }}>Status</th>
                    <th className="text-left py-3 px-2" style={{ color: '#0B0B0B', fontSize: '14px', fontWeight: '600' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event, index) => (
                    <tr key={event.id || index} style={{ borderBottom: '1px solid rgba(75, 157, 255, 0.1)' }}>
                      <td className="py-3 px-2" style={{ color: '#0B0B0B', fontSize: '13px' }}>
                        {new Date(event.created_at).toLocaleString()}
                      </td>
                      <td className="py-3 px-2" style={{ color: '#4B9DFF', fontSize: '13px' }}>
                        {formatEventType(event.event_type)}
                      </td>
                      <td className="py-3 px-2" style={{ color: '#47FFB0', fontSize: '13px', fontWeight: '600' }}>
                        {event.amount ? `$${event.amount.toFixed(2)}` : 'N/A'} {event.currency ? event.currency.toUpperCase() : ''}
                      </td>
                      <td className="py-3 px-2" style={{ color: '#0B0B0B', fontSize: '13px' }}>
                        {event.plan_or_bundle || 'N/A'}
                      </td>
                      <td className="py-3 px-2">
                        <span 
                          className="text-xs px-2 py-1 rounded"
                          style={{ 
                            background: `${getStatusColor(event.status)}20`, 
                            color: getStatusColor(event.status) 
                          }}
                        >
                          {event.status || 'completed'}
                        </span>
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex gap-2">
                          {event.idempotency_key && (
                            <button
                              onClick={() => copyToClipboard(event.idempotency_key, 'Idempotency Key')}
                              className="text-xs px-2 py-1 rounded hover:opacity-80"
                              style={{ background: 'rgba(75, 157, 255, 0.2)', color: '#4B9DFF' }}
                              title="Copy Idempotency Key"
                            >
                              {copiedId === 'Idempotency Key' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                            </button>
                          )}
                          {event.stripe_dashboard_url && (
                            <button
                              onClick={() => window.open(event.stripe_dashboard_url, '_blank')}
                              className="text-xs px-2 py-1 rounded hover:opacity-80"
                              style={{ background: 'rgba(71, 255, 176, 0.2)', color: '#47FFB0' }}
                              title="View in Stripe"
                            >
                              <ExternalLink className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile View */}
            <div className="md:hidden space-y-4">
              {events.map((event, index) => (
                <div 
                  key={event.id || index}
                  className="p-4 rounded-lg"
                  style={{ background: 'rgba(75, 157, 255, 0.05)', border: '1px solid rgba(75, 157, 255, 0.2)' }}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-semibold" style={{ color: '#4B9DFF', fontSize: '14px' }}>
                        {formatEventType(event.event_type)}
                      </p>
                      <p className="text-xs mt-1" style={{ color: '#0B0B0B', opacity: 0.7 }}>
                        {new Date(event.created_at).toLocaleString()}
                      </p>
                    </div>
                    <span 
                      className="text-xs px-2 py-1 rounded"
                      style={{ 
                        background: `${getStatusColor(event.status)}20`, 
                        color: getStatusColor(event.status) 
                      }}
                    >
                      {event.status || 'completed'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm font-semibold" style={{ color: '#47FFB0' }}>
                        {event.amount ? `$${event.amount.toFixed(2)}` : 'N/A'} {event.currency ? event.currency.toUpperCase() : ''}
                      </p>
                      <p className="text-xs" style={{ color: '#0B0B0B', opacity: 0.7 }}>
                        {event.plan_or_bundle || 'N/A'}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {event.idempotency_key && (
                        <button
                          onClick={() => copyToClipboard(event.idempotency_key, 'Idempotency Key')}
                          className="text-xs px-2 py-1 rounded"
                          style={{ background: 'rgba(75, 157, 255, 0.2)', color: '#4B9DFF' }}
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
